import { measureText, DocumentElement, ParagraphElement, DocSizeType  } from '@tencent/alloy-editor';
import { getFontSize, LEVEL } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-level-map';
import { getResiziable, getDefaultRect, setStylesWithElement, isParaElementEmpty, isParaEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { DEFAULT_TAIL_WHITESPACE_CALCULATION, FONT_SCALE } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { ITextBodyStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import {
    IMeasureSizeProps,
    IMeasureBlockSizeProps,
    IMeasureInlineSizeProps,
    IBatchMeasureBlockSizeProps,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import DataTransform from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/data-transform';
import { getLogicPropertyKeyMap, isVerticalDirection } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';

export const MAX_LEVEL = 10;
const MIN_LEVEL = 0;

export function getLevelByFontScale(fontScale: number): number {
    let level;
    Object.keys(LEVEL).forEach((key) => {
        if (LEVEL[key] === fontScale) {
            level = parseInt(key, 10);
        }
    });

    return level;
}

export default class ResizeMeasure {
    public static resizeLevelMap = new Map();
    public static blockSizeMap = new Map();  // 目前只有表格用到，后面可扩展

    public static getResizeLevel(editorProps: IEditorProps, prefFontScale = true): number | undefined {
        let resizeLevel;
        const autoFitType = editorProps.textBodyStyle?.autoFitType as string;
        const resiziable = getResiziable(autoFitType);
        if (!resiziable) return;
        if (prefFontScale && editorProps.textBodyStyle?.wrapperStyle?.fontScale) {
            resizeLevel = getLevelByFontScale(editorProps.textBodyStyle.wrapperStyle.fontScale);
        }
        if (typeof resizeLevel === 'undefined') {
            const editorValue = EditorDataModel.convertFromTextModel(editorProps, { noConvertBack: true });
            const { textRect, modelKey, isTitle } = editorProps as IMeasureSizeProps;
            const measureProps = {
                textRect,
                modelKey,
                isTitle,
                textBodyStyle: editorProps.textBodyStyle as ITextBodyStyle,
            };
            resizeLevel = ResizeMeasure.measureResizeLevel(editorValue, measureProps);
        }
        return resizeLevel;
    }

    /**
     * 根据宽高测量缩放等级
     * @param props
     */
    public static measureResizeLevel(documentValue: DocumentElement, measureProps: IMeasureSizeProps):
    number|undefined {
        const resizable = getResiziable(measureProps.textBodyStyle.autoFitType);
        if (!resizable) return;
        const editorBlockSize = this.getEditorResizeBlockSize(documentValue, measureProps);
        const firstRunFontSize = documentValue.children?.[0]?.children?.[0]?.fontSize;
        const key = this.getKey(measureProps, editorBlockSize, firstRunFontSize);
        if (this.resizeLevelMap.has(key)) {
            // 删除文本内容时会命中缓存， 此时可能需要放大字号在探测一次确定是否使用缓存
            const level = this.resizeLevelMap.get(key);
            if (level === MAX_LEVEL) {
                return level;
            }
            const cloneDocument = deepCopy(documentValue);
            this.updateParagraphList(cloneDocument.children, level + 1);
            const blockSize = this.getEditorResizeBlockSize(cloneDocument, measureProps);
            const { textDirection } = measureProps.textBodyStyle.wrapperStyle || {};
            const logicMap = getLogicPropertyKeyMap(textDirection);
            if (blockSize >= measureProps.textRect[logicMap.blockSize]) {
                return level;
            }
        }
        return this.measureTextBodyByBlockSize(documentValue, measureProps);
    }

    /**
     * 根据文本框逻辑高度（块级尺寸）计算内容缩放值
     * @param props
     */
    public static measureTextBodyByBlockSize(documentValue: DocumentElement, measureProps: IMeasureSizeProps):
    number|undefined {
        const { textRect, isTitle, textBodyStyle } = measureProps;
        const { textDirection } = textBodyStyle.wrapperStyle || {};
        const isVert = isVerticalDirection(textDirection);
        const cloneDocument = deepCopy(documentValue);
        const documentParagraphList = cloneDocument.children;
        const { height, width } = textRect || getDefaultRect();
        for (let level = MAX_LEVEL; level >= MIN_LEVEL; level--) {
            this.updateParagraphList(documentParagraphList, level);
            const firstRunFontSize = documentParagraphList?.[0]?.children?.[0]?.fontSize;
            const domBlockSize = this.getEditorResizeBlockSize(cloneDocument, measureProps);
            if (domBlockSize <= (isVert ? Math.ceil(width) : Math.ceil(height)) || level === MIN_LEVEL
                || (level === MAX_LEVEL - 1 && isTitle)) { // 母版标题自缩排只会缩小一个等级
                const key = this.getKey(measureProps, domBlockSize, firstRunFontSize);
                this.resizeLevelMap.set(key, level);
                return level;
            }
        }
    }

    /**
     * 根据 level 更新 editor-body 行间距和字体大小数据 以及项目符号尺寸
     * @param paragraphList
     * @param level
     */
    public static updateParagraphList(paragraphList: ParagraphElement[], level: number): void {
        paragraphList?.forEach((para) => {
            const textRuns = para.children;
            let firstTextRunFontSize = 18; // 记录第一个textRun的字体大小，后面用来计算
            Array.isArray(textRuns) && textRuns.forEach((run, index) => {
                const fontSize = getFontSize(run.displayFontSize, level);
                setStylesWithElement({ fontSize }, run);
                if (index === 0 && fontSize) {
                    firstTextRunFontSize = fontSize / FONT_SCALE;
                }
            });
            // 修改项目符号尺寸
            const character = para['origin-character'];
            if (character?.character && para.characterProps) {
                const  { buSzPct = 1 } = character;
                para.characterProps.fontSize = firstTextRunFontSize * buSzPct;
            }
            this.computeParaPadding(para, firstTextRunFontSize);
        });
    }

    /**
     * 批量获取文本高度
     * @param measureValues
     * @param option
     */
    public static batchGetEditorBlockSizes(
        measureValues: (IBatchMeasureBlockSizeProps | null)[],
        option?: { slideNum?: number, datetime?: Date },
    ): number[] {
        const blockSizeArray: number[] = [];
        measureValues.forEach((value: IBatchMeasureBlockSizeProps | null) => {
            if (value) {
                let blockSize;
                const blockKey = this.getEmptyBlockKey(value);
                if (blockKey) {
                    blockSize = this.blockSizeMap.get(blockKey);
                }
                if (!blockSize) {
                    const { textBodyStyle, width = 0, height = 0, paragraphList } = value;
                    const editorValue = EditorDataModel.convertFromTextModel({
                        paragraphList, textBodyStyle, slideNum: option?.slideNum, datetime: option?.datetime,
                    }, { noConvertBack: true });
                    const rect = { left: 0, top: 0, width, height };
                    blockSize = this.getEditorBlockSize(editorValue, { textRect: rect, textBodyStyle  });
                    blockKey && this.blockSizeMap.set(blockKey, blockSize);
                }
                blockSizeArray.push(blockSize);
            } else {
                blockSizeArray.push(0);
            }
        });
        return blockSizeArray;
    }

    /**
     * 获取自缩排尺寸时，要过滤数据结尾空行
     * @param value
     * @param measureProps
     */
    public static getEditorResizeBlockSize(value: DocumentElement, measureProps: IMeasureBlockSizeProps): number {
        const docValue = this.filterEndEmptyParagraph(value);
        return this.getEditorBlockSize(docValue, measureProps);
    }

    public static getEditorBlockSize(documentValue: DocumentElement, measureProps: IMeasureBlockSizeProps): number {
        const { width, height } = measureProps.textRect || getDefaultRect();
        const { textDirection } = measureProps.textBodyStyle?.wrapperStyle || {};
        const isVert = isVerticalDirection(textDirection);
        const measure = isVert ? height : width;
        const editorValue = { ...documentValue, tailWhitespaceCalculation: DEFAULT_TAIL_WHITESPACE_CALCULATION };
        const size = measure > 0 ? measureText(editorValue, { width, height })
            : { width: 0, height: 0 }; // 尺寸小于=0，不测量
        // 尺寸向上取整，避免高度不够导致下次排版结果不一致
        return isVert ? Math.ceil(size.width) : Math.ceil(size.height);
    }

    public static getEditorInlineSize(documentValue: DocumentElement, measureProps: IMeasureInlineSizeProps): number {
        const { textDirection } = measureProps.textBodyStyle.wrapperStyle || {};
        const { width, height } = measureProps.textRect;
        const isVert = isVerticalDirection(textDirection);
        const editorValue = {
            ...documentValue,
            docSizeType: DocSizeType.WrapContent,
            tailWhitespaceCalculation: DEFAULT_TAIL_WHITESPACE_CALCULATION,
        };
        const size = measureText(editorValue, { width, height });
        // inline尺寸需要尽可能大。否则下次测量宽度不够，可能会导致高度变大，出现换行。
        return isVert ? Math.ceil(size.height) : Math.ceil(size.width);
    }

    /**
     * 自缩排过滤结尾空行
     * @param documentValue
     */
    private static filterEndEmptyParagraph(documentValue: DocumentElement): DocumentElement {
        // 多栏时不需要过滤结尾空行
        if (documentValue.columnNumber && documentValue.columnNumber > 1) {
            return documentValue;
        }
        if (Array.isArray(documentValue.children)) {
            let endIndex = -1;
            for (let i = documentValue.children.length - 1; i >= 0; i --) {
                if (isParaElementEmpty(documentValue.children[i])) {
                    endIndex = i;
                } else {
                    break;
                }
            }
            if (endIndex !== -1) {
                const noEndEmptyParas: ParagraphElement[] = documentValue.children.slice(0, endIndex);
                return { ...documentValue, children: noEndEmptyParas };
            }
        }
        return documentValue;
    }

    /**
     * 根据文字大小更新 paddingTop & paddingBottom
     * @param pStyle
     * @param firstTextRunFontSize
     */
    private static computeParaPadding(para, firstTextRunFontSize) {
        const pStyle = DataTransform.getOriginProps(para) as any;
        const styles = {} as any;
        if (pStyle.paddingTop && pStyle.paddingTopIs === 'percent') {
            styles.paddingTop = pStyle.rawPaddingTop * firstTextRunFontSize * 1.2;
        }
        if (pStyle.paddingBottom && pStyle.paddingBottomIs === 'percent') {
            styles.paddingBottom = pStyle.rawPaddingBottom * firstTextRunFontSize * 1.2;
        }
        setStylesWithElement(styles, para);
    }

    private static getKey(measureProps: IMeasureSizeProps, blockSize: number, fontSize) {
        const { textBodyStyle, textRect } = measureProps;
        const { width, height } = textRect || getDefaultRect();
        const { textDirection } = textBodyStyle.wrapperStyle || {};
        const isVert = isVerticalDirection(textDirection);
        return `${measureProps.modelKey}_${Math.ceil(isVert ? width : height)}_${Math.ceil(blockSize)}_${fontSize}_${Number(isVert)}`;
    }

    /**
     * 获取空文本高度计算缓存的key。
     * 缓存关键属性：字体大小，行高，是否竖排
     * @param value
     */
    private static getEmptyBlockKey(value: IBatchMeasureBlockSizeProps): string {
        let key = '';
        const { textBodyStyle, paragraphList = [] } = value;
        if (paragraphList?.length <= 1 && isParaEmpty(paragraphList[0])) {
            const textDirection = textBodyStyle?.wrapperStyle?.textDirection;
            const fontSize = paragraphList[0]?.textRuns?.[0]?.styles?.fontSize
                || paragraphList[0]?.endParaRPr?.fontSize || 0;
            const lineHeight = paragraphList[0]?.paragraphProps?.lineHeight || 0;
            const isVert = isVerticalDirection(textDirection);
            key = `${fontSize}-${lineHeight}-${isVert ? 1 : 0}`;
        }
        return key;
    }
}
